// ABOUTME: Tooltip component for profile/metabolic settings hints
// ABOUTME: Displays term definition with optional sections for details and notes

import { memo } from "react"
import { TooltipSection } from "./TooltipSection"
import { TooltipDivider } from "./TooltipDivider"
import { TooltipHighlight } from "./TooltipHighlight"
import DOMPurify from "dompurify"

interface ProfileHintSection {
  title?: string
  content: string
  isHtml?: boolean
}

interface ProfileHintTooltipProps {
  term: string
  definition: string
  sections?: ProfileHintSection[]
  importantNote?: string
  className?: string
}

export const ProfileHintTooltip = memo(
  ({ term, definition, sections, importantNote, className = "" }: ProfileHintTooltipProps) => {
    return (
      <div className={`max-w-[300px] min-w-[240px] ${className}`}>
        {/* Term header */}
        <div className="font-semibold text-[13px] mb-1">{term}</div>
        <TooltipDivider />

        {/* Main definition */}
        <TooltipSection>
          <div
            className="whitespace-normal"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(definition) }}
          />
        </TooltipSection>

        {/* Additional sections */}
        {sections?.map((section, index) => (
          <TooltipSection
            key={index}
            title={section.title}
            titleColor="text-gray-500"
          >
            {section.isHtml ? (
              <div
                className="whitespace-normal [&>li]:ml-3 [&>li]:list-disc"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }}
              />
            ) : (
              section.content
            )}
          </TooltipSection>
        ))}

        {/* Important note */}
        {importantNote && <TooltipHighlight type="info">{importantNote}</TooltipHighlight>}
      </div>
    )
  },
)

ProfileHintTooltip.displayName = "ProfileHintTooltip"

// Simplified version for basic hints with just description
interface SimpleHintTooltipProps {
  description: string
  isHtml?: boolean
  maxWidth?: string
  className?: string
}

export const SimpleHintTooltip = memo(
  ({ description, isHtml = true, maxWidth = "280px", className = "" }: SimpleHintTooltipProps) => {
    return (
      <div
        className={`min-w-[200px] text-white ${className}`}
        style={{ maxWidth }}
      >
        {isHtml ? (
          <div
            className="text-[13px] leading-[18px] whitespace-normal [&>p]:mb-2 [&>p:last-child]:mb-0 [&>li]:ml-3 [&>li]:list-disc"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
          />
        ) : (
          <div className="text-[13px] leading-[18px] whitespace-normal">{description}</div>
        )}
      </div>
    )
  },
)

SimpleHintTooltip.displayName = "SimpleHintTooltip"
